import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { adminStatsAPI } from '../services/adminApi';
import { useAdminSeasonPhaseFilter } from '../hooks/useAdminSeasonPhaseFilter';
import AdminSeasonPhaseFilters from './AdminSeasonPhaseFilters';

const BRAND = { blue: '#043663', muted: '#64748b' };

const Page = styled.div`
  width: 100%;
  max-width: none;
  min-width: 0;
`;

const Header = styled.div`
  margin-bottom: 1rem;
  h3 {
    margin: 0 0 0.35rem;
    font-size: 1.25rem;
    font-weight: 800;
    color: #0f172a;
  }
  p {
    margin: 0;
    color: ${BRAND.muted};
    font-size: 0.875rem;
    line-height: 1.45;
    max-width: 52rem;
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  align-items: flex-end;
  margin-bottom: 1rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #475569;
  }
  select {
    min-width: 14rem;
    padding: 0.5rem 0.65rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.875rem;
  }
`;

const Btn = styled.button`
  background: ${BRAND.blue};
  color: white;
  border: none;
  padding: 0.55rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ScrollHint = styled.p`
  display: none;
  font-size: 0.75rem;
  color: ${BRAND.muted};
  margin: 0 0 0.5rem;

  @media (max-width: 1200px) {
    display: block;
  }
`;

const TableShell = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
`;

const scrollbarStyles = `
  scrollbar-width: thin;
  scrollbar-color: #94a3b8 #e2e8f0;

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #e2e8f0;
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb {
    background: #64748b;
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #475569;
  }
`;

const TopScroller = styled.div`
  overflow-x: scroll;
  overflow-y: hidden;
  height: 12px;
  margin-bottom: 6px;
  ${scrollbarStyles}
`;

const TopScrollerInner = styled.div`
  height: 1px;
`;

const TableWrap = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  cursor: ${(p) => (p.$canScroll ? (p.$dragging ? 'grabbing' : 'grab') : 'default')};
  user-select: ${(p) => (p.$dragging ? 'none' : 'auto')};

  ${scrollbarStyles}
`;

const Table = styled.table`
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.8rem;

  th,
  td {
    padding: 0.6rem 0.9rem;
    border-bottom: 1px solid #e5e7eb;
    text-align: left;
    vertical-align: middle;
    white-space: nowrap;
    line-height: 1.3;
  }

  th {
    font-weight: 700;
    background: #f8fafc;
    color: #0f172a;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  td {
    color: #334155;
    background: #fff;
  }

  tbody tr:hover td {
    background: #f8fafc;
  }

  a {
    white-space: nowrap;
  }

  th:nth-child(1),
  td:nth-child(1) {
    position: sticky;
    left: 0;
    z-index: 3;
    min-width: 10.5rem;
    box-shadow: 1px 0 0 #e5e7eb;
  }

  th:nth-child(1) {
    z-index: 4;
    background: #f8fafc;
  }
`;

const EmptyCell = styled.td`
  color: ${BRAND.muted};
  padding: 0.75rem 0.9rem;
`;

const FILTER_OPTIONS = [
  { value: 'all', label: 'All (pending + paid)' },
  { value: 'pending_payment', label: 'Pending payment' },
  { value: 'one_player_team', label: 'One-player team' },
  { value: 'missing_second_payment', label: 'Missing second payment' },
  { value: 'complete_paid', label: 'Complete and paid team' },
];

const FILTER_HELP = {
  all: 'Every pending and paid registration invoice for this phase.',
  pending_payment:
    'Players who still owe a payment for this phase. Abandoned or retry invoices are hidden if that player already paid.',
  one_player_team:
    'Teams with exactly one player on the roster. Shows that player plus any pending partner invite.',
  missing_second_payment:
    'Teams still awaiting the second player payment (awaiting partner, or fewer than 2 paid slots).',
  complete_paid: 'Teams with both players paid and registration marked complete.',
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function useHorizontalTableScroll() {
  const tableWrapRef = useRef(null);
  const topScrollRef = useRef(null);
  const syncingRef = useRef(false);
  const draggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollRef = useRef(0);
  const didDragRef = useRef(false);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [canScroll, setCanScroll] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [wrapEl, setWrapEl] = useState(null);

  const measure = useCallback(() => {
    const el = tableWrapRef.current;
    if (!el) return;
    setScrollWidth(el.scrollWidth);
    setCanScroll(el.scrollWidth > el.clientWidth + 1);
  }, []);

  const syncScroll = useCallback((from, left) => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    if (from !== 'table' && tableWrapRef.current) {
      tableWrapRef.current.scrollLeft = left;
    }
    if (from !== 'top' && topScrollRef.current) {
      topScrollRef.current.scrollLeft = left;
    }
    syncingRef.current = false;
  }, []);

  const setTableWrapNode = useCallback(
    (node) => {
      tableWrapRef.current = node;
      setWrapEl(node);
      if (!node) return;
      measure();
    },
    [measure]
  );

  useEffect(() => {
    if (!wrapEl) return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(wrapEl);
    if (wrapEl.firstElementChild) ro.observe(wrapEl.firstElementChild);
    window.addEventListener('resize', measure);
    measure();
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure, wrapEl]);

  const onTopScroll = (e) => syncScroll('top', e.currentTarget.scrollLeft);
  const onTableScroll = (e) => syncScroll('table', e.currentTarget.scrollLeft);

  const endDrag = useCallback(() => {
    draggingRef.current = false;
    setDragging(false);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current || !tableWrapRef.current) return;
      const dx = e.clientX - dragStartXRef.current;
      if (Math.abs(dx) > 4) didDragRef.current = true;
      tableWrapRef.current.scrollLeft = dragStartScrollRef.current - dx;
      syncScroll('table', tableWrapRef.current.scrollLeft);
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      endDrag();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('mouseup', onUp);
    };
  }, [endDrag, syncScroll]);

  const onPointerDown = (e) => {
    if (e.pointerType === 'touch') return;
    if (e.button != null && e.button !== 0) return;
    const target = e.target instanceof Element ? e.target : e.target.parentElement;
    if (target?.closest('a, button, input, select, textarea')) return;
    const wrap = tableWrapRef.current;
    if (!wrap || wrap.scrollWidth <= wrap.clientWidth) return;
    draggingRef.current = true;
    didDragRef.current = false;
    dragStartXRef.current = e.clientX;
    dragStartScrollRef.current = wrap.scrollLeft;
    setDragging(true);
  };

  const onClickCapture = (e) => {
    if (!didDragRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    didDragRef.current = false;
  };

  return {
    tableWrapRef: setTableWrapNode,
    topScrollRef,
    scrollWidth,
    canScroll,
    dragging,
    measure,
    onTopScroll,
    onTableScroll,
    onPointerDown,
    onClickCapture,
  };
}

export default function AdminRegistrationPaymentExportSection() {
  const { campaignId, seasonId, onCampaignChange, onPhaseChange, seasonOptions, phaseOptions } =
    useAdminSeasonPhaseFilter({ allowAll: false, autoDefault: true });

  const [filter, setFilter] = useState('pending_payment');
  const [exporting, setExporting] = useState(false);
  const {
    tableWrapRef,
    topScrollRef,
    scrollWidth,
    canScroll,
    dragging,
    measure,
    onTopScroll,
    onTableScroll,
    onPointerDown,
    onClickCapture,
  } = useHorizontalTableScroll();

  const queryKey = useMemo(() => ['admin-registration-payments', seasonId, filter], [seasonId, filter]);

  const { data, isLoading, isFetching, refetch } = useQuery(
    queryKey,
    () => adminStatsAPI.getRegistrationPayments({ seasonId, filter }),
    { enabled: !!seasonId, retry: false, keepPreviousData: true }
  );

  const rows = data?.data?.rows || [];
  const count = data?.data?.count ?? rows.length;

  useEffect(() => {
    measure();
  }, [count, filter, seasonId, measure]);

  const handleExport = async () => {
    if (!seasonId) {
      toast.error('Select a phase first.');
      return;
    }
    setExporting(true);
    try {
      const blob = await adminStatsAPI.downloadRegistrationPaymentsCsv({ seasonId, filter });
      if (blob && blob.type && blob.type.includes('application/json')) {
        const text = await blob.text();
        let msg = 'Export failed';
        try {
          msg = JSON.parse(text).message || msg;
        } catch (_) {
          /* ignore */
        }
        toast.error(msg);
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `registration-payments-${filter}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Page>
      <Header>
        <h3>Registration payment status</h3>
        <p>
          Players and teams for the selected phase with registration payment status. Export includes
          contact details, canton/division, team status, payment status, payment start date, and a
          direct payment link when still pending.
        </p>
      </Header>

      <AdminSeasonPhaseFilters
        campaignId={campaignId}
        seasonId={seasonId}
        onCampaignChange={onCampaignChange}
        onPhaseChange={onPhaseChange}
        seasonOptions={seasonOptions}
        phaseOptions={phaseOptions}
      />

      <Toolbar>
        <Field>
          <label htmlFor="reg-pay-filter">Filter</label>
          <select
            id="reg-pay-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            {FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Btn type="button" onClick={() => refetch()} disabled={!seasonId || isFetching}>
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </Btn>
        <Btn type="button" onClick={handleExport} disabled={!seasonId || exporting}>
          {exporting ? 'Exporting…' : 'Export CSV'}
        </Btn>
      </Toolbar>
      <p style={{ fontSize: '0.8rem', color: BRAND.muted, margin: '-0.35rem 0 1rem' }}>
        {FILTER_HELP[filter]}
      </p>

      {!seasonId ? (
        <p style={{ color: BRAND.muted, fontSize: '0.875rem' }}>Select a phase to load data.</p>
      ) : isLoading ? (
        <p style={{ color: BRAND.muted, fontSize: '0.875rem' }}>Loading…</p>
      ) : (
        <>
          <p style={{ fontSize: '0.8rem', color: BRAND.muted, margin: '0 0 0.75rem' }}>
            {count} row{count === 1 ? '' : 's'}
          </p>
          <ScrollHint>
            Drag the table sideways, or use the thin bar above the headers, to see every column.
          </ScrollHint>
          <TableShell>
            {canScroll ? (
              <TopScroller
                ref={topScrollRef}
                onScroll={onTopScroll}
                aria-label="Table horizontal scroll"
              >
                <TopScrollerInner style={{ width: scrollWidth }} />
              </TopScroller>
            ) : null}
            <TableWrap
              ref={tableWrapRef}
              $canScroll={canScroll}
              $dragging={dragging}
              onScroll={onTableScroll}
              onPointerDown={onPointerDown}
              onClickCapture={onClickCapture}
              onDragStart={(e) => e.preventDefault()}
            >
              <Table>
                <thead>
                  <tr>
                    {[
                      'Team',
                      'Player',
                      'Email',
                      'Phone',
                      'Canton',
                      'Division',
                      'Team status',
                      'Payment',
                      'Started',
                      'Link',
                    ].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <EmptyCell colSpan={10}>No rows for this filter.</EmptyCell>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.invoiceId}>
                        <td>{row.teamName || '—'}</td>
                        <td>
                          {[row.playerFirstName, row.playerLastName].filter(Boolean).join(' ') || '—'}
                        </td>
                        <td>{row.playerEmail || '—'}</td>
                        <td>{row.playerPhone || '—'}</td>
                        <td>{row.canton || '—'}</td>
                        <td>{row.division || '—'}</td>
                        <td>{row.teamStatus}</td>
                        <td>{row.playerPaymentStatus}</td>
                        <td>{formatDate(row.paymentInitiatedAt)}</td>
                        <td>
                          {row.paymentLink ? (
                            <a href={row.paymentLink} target="_blank" rel="noopener noreferrer">
                              Pay / register
                            </a>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </TableWrap>
          </TableShell>
        </>
      )}
    </Page>
  );
}
